/**
 * CompanySectionNav — navigation sidebar gauche desktop pour pages Create/Edit.
 *
 * Pattern UX commun établi 9.P.2.R1 — anchor-links cliquables + scroll-spy
 * via IntersectionObserver natif (zéro dépendance).
 *
 * Comportement :
 *   - Click anchor → scroll smooth vers la section (CSS `scroll-behavior: smooth`
 *     hérité du body global, ou `el.scrollIntoView({ behavior: "smooth" })`).
 *   - IntersectionObserver détecte la section visible → highlight active dans le nav.
 *   - Mobile (< lg) : caché (le user scrolle naturellement).
 *
 * a11y :
 *   - `<nav aria-label>` landmark
 *   - `<a href="#anchorId">` standard anchor links accessibles clavier
 *   - `aria-current="true"` sur item actif (lecteur d'écran)
 *   - focus-visible:ring-brand-500
 *
 * Performance :
 *   - 1 IntersectionObserver partagé pour tous les anchors
 *   - rootMargin négatif top pour déclencher quand section centre visible
 */

import { useEffect, useRef, useState } from "react";

import { cn } from "../../../lib/cn";

export type CompanySectionNavItem = {
  readonly anchorId: string;
  readonly label: string;
};

type Props = {
  readonly items: ReadonlyArray<CompanySectionNavItem>;
  readonly ariaLabel: string;
  readonly className?: string;
};

export function CompanySectionNav({ items, ariaLabel, className }: Props) {
  const [activeId, setActiveId] = useState<string | null>(
    items[0]?.anchorId ?? null,
  );
  const navRef = useRef<HTMLElement | null>(null);

  // IntersectionObserver scroll-spy.
  useEffect(() => {
    if (typeof window === "undefined" || !window.IntersectionObserver) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        // Trouver la section la plus visible (top de viewport).
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0 && visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: [0, 0.5, 1],
      },
    );
    for (const item of items) {
      const el = document.getElementById(item.anchorId);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [items]);

  // Click sur anchor → scroll smooth vers section + update active.
  const onClick = (e: React.MouseEvent<HTMLAnchorElement>, anchorId: string) => {
    const el = document.getElementById(anchorId);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveId(anchorId);
    // Update URL hash sans déclencher scroll natif.
    if (typeof window !== "undefined" && window.history.replaceState) {
      window.history.replaceState(null, "", `#${anchorId}`);
    }
  };

  return (
    <nav
      ref={navRef}
      aria-label={ariaLabel}
      className={cn("hidden lg:block", className)}
    >
      <ul className="space-y-1">
        {items.map((item) => {
          const isActive = activeId === item.anchorId;
          return (
            <li key={item.anchorId}>
              <a
                href={`#${item.anchorId}`}
                onClick={(e) => onClick(e, item.anchorId)}
                aria-current={isActive ? "true" : undefined}
                className={cn(
                  "block rounded-md px-3 py-2 text-sm font-medium transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
                  isActive
                    ? "bg-brand-50 text-brand-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                )}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
